import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@paulownia/shared/components/ui/dialog';
import { Button } from '@paulownia/shared/components/ui/button';
import { Badge } from '@paulownia/shared/components/ui/badge';
import { Clock, CheckCircle2, CreditCard, Euro, Edit, Trash2, StickyNote, AlertCircle, Phone, Repeat } from 'lucide-react';
import { Order, Product, calculateOrderTotal } from '@paulownia/shared/types';
import { validateOrder } from '@paulownia/shared/services/orders';
import { createRecurringOrderFromOrder } from '@paulownia/shared/services/recurring-orders';
import { toast } from 'sonner';
import { useState } from 'react';
import { OrderLineCard } from './OrderLineCard';

interface OrderDetailModalProps {
    order: Order | null;
    isOpen: boolean;
    onClose: () => void;
    products: Product[];
    onStatusChange: (orderId: string, newStatus: 'ready' | 'done') => void;
    onMarkAllPreparedAndReady: (order: Order) => void;
    onTogglePaid: (orderId: string, currentStatus: boolean) => void;
    onTogglePrepared?: (order: Order, lineIndex: number) => void;
    onEdit?: (order: Order) => void;
    onDelete?: (orderId: string) => void;
}

export function OrderDetailModal({
    order,
    isOpen,
    onClose,
    products,
    onStatusChange,
    onMarkAllPreparedAndReady,
    onTogglePaid,
    onTogglePrepared,
    onEdit,
    onDelete,
}: OrderDetailModalProps) {
    const [makingRecurring, setMakingRecurring] = useState(false);

    if (!order) return null;

    const total = calculateOrderTotal(order.lines, products);
    const pickupDate = new Date(order.pickupAtISO);

    const handleMakeRecurring = async () => {
        if (!order.marketId) {
            toast.error('Cette commande n\'est associée à aucun marché.');
            return;
        }
        try {
            setMakingRecurring(true);
            await createRecurringOrderFromOrder(order);
            toast.success('Commande rendue récurrente chaque semaine.');
            onClose();
        } catch (e) {
            const error = e as Error;
            toast.error(error.message || 'Erreur lors de la création de la récurrence.');
        } finally {
            setMakingRecurring(false);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="text-xl">{order.customerName}</DialogTitle>
                </DialogHeader>

                <div className="space-y-4 py-2">
                    {/* Infos principales */}
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-gray-600">
                            <Clock className="h-4 w-4" />
                            <span>
                                Récupération à{' '}
                                <strong>
                                    {pickupDate.toLocaleTimeString('fr-FR', {
                                        hour: '2-digit',
                                        minute: '2-digit',
                                    })}
                                </strong>
                            </span>
                        </div>
                        <div className="flex items-center gap-2">
                            {order.paid && (
                                <Badge className="bg-green-600">
                                    <CheckCircle2 className="h-3 w-3 mr-1" />
                                    Payé
                                </Badge>
                            )}
                            {order.status === 'awaiting_validation' && (
                                <Badge className="bg-amber-500">
                                    <AlertCircle className="h-3 w-3 mr-1" />
                                    Nouvelle
                                </Badge>
                            )}
                            {order.status === 'pending' && <Badge variant="secondary">À préparer</Badge>}
                            {order.status === 'ready' && <Badge variant="green">Prête</Badge>}
                            {order.status === 'done' && (
                                <Badge variant="outline" className="text-gray-500">
                                    Récupérée
                                </Badge>
                            )}
                        </div>
                    </div>

                    {/* Phone number */}
                    {order.phoneNumber && (
                        <div className="flex items-center gap-2 text-gray-600">
                            <Phone className="h-4 w-4" />
                            <span>{order.phoneNumber}</span>
                        </div>
                    )}

                    {/* Validation Alert */}
                    {order.status === 'awaiting_validation' && (
                        <div className="rounded-lg bg-amber-100 border-2 border-amber-400 p-4">
                            <div className="flex items-center gap-2 text-amber-800 font-medium mb-2">
                                <AlertCircle className="h-5 w-5" />
                                Nouvelle commande - en attente de validation
                            </div>
                            <p className="text-sm text-amber-700 mb-3">
                                Cette commande a été passée par un client et nécessite votre validation.
                            </p>
                            <div className="flex gap-2">
                                <Button
                                    onClick={async () => {
                                        await validateOrder(order.id);
                                        onClose();
                                    }}
                                    className="bg-green-600 hover:bg-green-700"
                                >
                                    <CheckCircle2 className="h-4 w-4 mr-1" />
                                    Valider la commande
                                </Button>
                                {onEdit && (
                                    <Button variant="outline" onClick={() => onEdit(order)}>
                                        <Edit className="h-4 w-4 mr-1" />
                                        Modifier
                                    </Button>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Note */}
                    {order.note && (
                        <div className="rounded-lg bg-amber-50 border border-amber-200 p-3">
                            <div className="text-xs font-medium text-amber-700 mb-1 flex items-center gap-1">
                                <StickyNote className="h-3 w-3" />
                                Note
                            </div>
                            <p className="text-sm text-amber-900 whitespace-pre-wrap">{order.note}</p>
                        </div>
                    )}

                    {/* Liste des produits */}
                    <div className="space-y-2">
                        <div className="font-medium text-gray-700">Produits</div>
                        {order.lines.map((line, idx) => {
                            const product = products.find((p) => p.id === line.productId);
                            return (
                                <OrderLineCard
                                    key={idx}
                                    line={line}
                                    product={product}
                                    allProducts={products}
                                    showCheckbox={!!onTogglePrepared}
                                    isPrepared={line.preparedQuantity >= line.quantity}
                                    onTogglePrepared={onTogglePrepared ? () => onTogglePrepared(order, idx) : undefined}
                                />
                            );
                        })}
                    </div>

                    {/* Total - Moved to bottom */}
                    {total !== null && (
                        <div className="rounded-lg bg-green-50 border border-green-200 p-4 flex items-center justify-between">
                            <span className="text-green-900 font-medium flex items-center gap-2">
                                <Euro className="h-5 w-5" />
                                Total TTC
                            </span>
                            <span className="text-2xl font-bold text-green-700">{total.toFixed(2)} €</span>
                        </div>
                    )}

                    {/* Actions */}
                    <div className="border-t pt-4 space-y-3">
                        <div className="grid grid-cols-2 gap-2">
                            {order.status === 'pending' && (
                                <Button onClick={() => onMarkAllPreparedAndReady(order)} className="w-full">
                                    Marquer prête
                                </Button>
                            )}
                            {order.status === 'ready' && (
                                <Button
                                    variant="default"
                                    onClick={() => onStatusChange(order.id, 'done')}
                                    className="w-full bg-green-600 hover:bg-green-700"
                                >
                                    Marquer récupérée
                                </Button>
                            )}
                            <Button
                                variant={order.paid ? 'default' : 'outline'}
                                onClick={() => onTogglePaid(order.id, order.paid)}
                                className={order.paid ? 'bg-green-600 hover:bg-green-700' : ''}
                            >
                                {order.paid ? (
                                    <>
                                        <CheckCircle2 className="h-4 w-4 mr-1" />
                                        Payé
                                    </>
                                ) : (
                                    <>
                                        <CreditCard className="h-4 w-4 mr-1" />
                                        Marquer payé
                                    </>
                                )}
                            </Button>
                        </div>

                        <div className="grid grid-cols-2 gap-2">
                            {onEdit && (
                                <Button variant="outline" onClick={() => onEdit(order)}>
                                    <Edit className="h-4 w-4 mr-1" />
                                    Modifier
                                </Button>
                            )}
                            {onDelete && (
                                <Button variant="destructive" onClick={() => onDelete(order.id)}>
                                    <Trash2 className="h-4 w-4 mr-1" />
                                    Supprimer
                                </Button>
                            )}
                        </div>

                        {/* Recurring order */}
                        {!order.recurringOrderId && order.marketId && (
                            <Button
                                variant="outline"
                                className="w-full border-orange-300 text-orange-700 hover:bg-orange-50"
                                onClick={handleMakeRecurring}
                                disabled={makingRecurring}
                            >
                                <Repeat className="h-4 w-4 mr-2" />
                                {makingRecurring ? 'Création...' : 'Rendre récurrent (chaque semaine)'}
                            </Button>
                        )}
                        {order.recurringOrderId && (
                            <div className="flex items-center gap-2 text-sm text-orange-600 bg-orange-50 rounded-lg px-3 py-2">
                                <Repeat className="h-4 w-4 flex-shrink-0" />
                                <span>Commande récurrente active</span>
                            </div>
                        )}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
